/**
 * S8's contribution to the storage tree-extension registry (Task 8.12): on a container node of an account whose
 * hierarchical namespace is confirmed — and on a directory-SAS attachment's root node — **Open blob view** (the Blob
 * view with an EXPLICIT intent, never re-routed to Data Lake) and **Copy abfss:// URI**. Absent on non-HNS and
 * unknown accounts (a capability the account lacks is never a placeholder). The tree's own Open follows the default
 * routing (Data Lake view on HNS accounts).
 */
import type { ContextMenuAction } from '../../../../explorer/models/TreeModel';
import type { HnsPathRef } from '../../../../../types/azureStorage';
import type { StorageContextMenuDeps, StorageMenuNode } from '../../../../database-objects/services/storageContextMenuService';
import { registerStorageTreeExtension } from '../../../../database-objects/services/storageTreeExtensions';
import { getStorageAccountInfoSnapshot } from '../../../../database-objects/store/storageAccountInfoStore';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { openStorageExplorerTab } from '../../utils/openStorageExplorerTab';
import { copyHnsPath } from '../hnsRowMenuItems';
import { hnsDirectoryOfBlobRoot } from '../hnsView';

export const S8_OPEN_BLOB_VIEW_ACTION = 's8-open-blob-view';
export const S8_COPY_ABFSS_ACTION = 's8-copy-abfss';

/** The Data Lake directory a tree node stands for, when the account is confirmed HNS; else `undefined`. */
export function hnsTreeTarget(node: StorageMenuNode): HnsPathRef | undefined {
  if (node.type !== 'storage-container' && node.type !== 'pinned-storage') return undefined;
  if (getStorageAccountInfoSnapshot(node.metadata.connectionId)?.hns !== 'yes') return undefined;
  const resource = node.metadata.resource;
  if (resource.kind === 'hnsPath') return resource.isDirectory ? resource : undefined;
  return hnsDirectoryOfBlobRoot(resource);
}

export function s8TreeMenuItems(node: StorageMenuNode): ContextMenuAction[] {
  if (!hnsTreeTarget(node)) return [];
  return [
    { id: S8_OPEN_BLOB_VIEW_ACTION, label: 'Open blob view', icon: 'folder-open' }, // F4: a folder view, not a table
    { id: S8_COPY_ABFSS_ACTION, label: 'Copy abfss:// URI', icon: 'link-2' },
  ];
}

/** `true` when the action is S8's (handled here). */
export function executeS8StorageTreeAction(actionId: string, node: StorageMenuNode, _deps?: Pick<StorageContextMenuDeps, 'track'>): boolean {
  if (actionId !== S8_OPEN_BLOB_VIEW_ACTION && actionId !== S8_COPY_ABFSS_ACTION) return false;
  const target = hnsTreeTarget(node);
  if (!target) return true;
  if (actionId === S8_COPY_ABFSS_ACTION) {
    copyHnsPath(node.metadata.connectionId, target, 'abfss', 'tree');
    return true;
  }
  trackStorageEvent('ui:storage_hns_view', { view: 'blob' });
  openStorageExplorerTab({ connectionId: node.metadata.connectionId, root: target, viewIntent: 'blob', source: node.type === 'pinned-storage' ? 'pin' : 'tree' });
  return true;
}

export function registerHnsTreeExtension(): () => void {
  return registerStorageTreeExtension({ id: 's8-hns', slice: 's8', menuItems: s8TreeMenuItems });
}
